// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Card-based list for version diff annotations.
 * Shows Was/Now/Impact for each change.
 */
import { type FC } from 'react'
import { cn, Card, CardContent, Badge } from '@/components/ui'
import { FileText, AlertCircle } from 'lucide-react'

export interface VersionChange {
  id: string
  pageNumber: number
  oldText: string | null
  newText: string | null
  impact: string | null
  suggestions: string | null
  riskLevel: string | null
}

interface DiffChangesListProps {
  changes: VersionChange[]
  className?: string
}

export const DiffChangesList: FC<DiffChangesListProps> = ({ changes, className }) => {
  if (changes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <AlertCircle className="h-6 w-6 mb-3" />
        <p className="text-sm text-center max-w-md">
          No version changes found. Run the version comparison pipeline first.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4 overflow-auto p-4', className)}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold">Version Changes</h3>
        <Badge variant="secondary" className="font-mono text-xs">
          {changes.length} {changes.length === 1 ? 'change' : 'changes'}
        </Badge>
      </div>

      {changes.map((change) => (
        <DiffChangeCard key={change.id} change={change} />
      ))}
    </div>
  )
}

const RISK_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  NEGLIGIBLE: 'secondary',
  LOW: 'secondary',
  MODERATE: 'outline',
  HIGH: 'destructive',
  SEVERE: 'destructive',
}

const DiffChangeCard: FC<{ change: VersionChange }> = ({ change }) => {
  return (
    <Card className="bg-card text-card-foreground border">
      <CardContent className="p-4 space-y-3">
        {/* Page reference */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <FileText className="h-3.5 w-3.5" />
          <span>Page {change.pageNumber}</span>
        </div>

        {/* Was / Now comparison */}
        <div className="grid gap-3 sm:grid-cols-2">
          {/* Was (old text) */}
          {change.oldText && (
            <div className="rounded-md bg-destructive/10 p-3 border border-destructive/30">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-destructive">
                Was
              </span>
              <p className="mt-1.5 text-sm text-foreground line-through decoration-destructive/50">
                {change.oldText}
              </p>
            </div>
          )}

          {/* Now (new text) */}
          {change.newText && (
            <div className="rounded-md bg-muted p-3 border">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Now
              </span>
              <p className="mt-1.5 text-sm text-foreground">
                {change.newText}
              </p>
            </div>
          )}
        </div>

        {/* Impact */}
        {change.impact && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-border pl-3 py-1">
            {change.impact}
          </p>
        )}

        {/* Suggestions */}
        {change.suggestions && (
          <div className="rounded-md bg-primary/5 p-3 border border-primary/20">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              Suggestion
            </span>
            <p className="mt-1.5 text-sm text-foreground">
              {change.suggestions}
            </p>
          </div>
        )}

        {/* Risk Level */}
        {change.riskLevel && (
          <Badge variant={RISK_VARIANT[change.riskLevel] ?? 'outline'} className="text-xs w-fit">
            {change.riskLevel}
          </Badge>
        )}
      </CardContent>
    </Card>
  )
}
